import { useState, useRef, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/auth';
import { api } from '@/services/api';
import {
    Target,
    LayoutDashboard,
    Kanban,
    BarChart3,
    PieChart,
    Settings,
    LogOut,
    Menu,
    X,
    Bell,
    Search,
    Check,
    CheckCheck,
    BookOpen,
    MessageSquare,
    UserPlus,
    Crown,
    Link2,
    FileText,
} from 'lucide-react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import OnboardingTour from '@/components/OnboardingTour';
import { useIdleTimeout, IdleTimeoutWarning } from '@/hooks/useIdleTimeout';
import { WorkspaceProvider, useWorkspace } from '@/contexts/WorkspaceContext';
import WorkspaceSwitcher from '@/components/WorkspaceSwitcher';
import ThemeToggle from '@/components/ThemeToggle';
import PlanBadge from '@/components/PlanBadge';
import AIChatWidget from '@/components/AIChatWidget';

function AppLayoutContent() {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [notificationsOpen, setNotificationsOpen] = useState(false);
    const { user, logout, updateUser } = useAuthStore();
    const navigate = useNavigate();
    const notificationsRef = useRef<HTMLDivElement>(null);

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    // Idle timeout for auto-logout after 15 minutes of inactivity
    const { showWarning, remainingTime, extendSession } = useIdleTimeout();

    // Refresh user data on mount to pick up any plan changes from admin
    useEffect(() => {
        const refreshUserData = async () => {
            try {
                const data = await api.getMe();
                if (data?.user) {
                    updateUser({
                        full_name: data.user.full_name,
                        avatar_url: data.user.avatar_url,
                        plan: data.user.plan,
                    });
                }
            } catch (error) {
                // Ignore errors - user will use cached data
                console.debug('Failed to refresh user data:', error);
            }
        };
        refreshUserData();
    }, [updateUser]);

    // Close notifications when clicking outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (notificationsRef.current && !notificationsRef.current.contains(event.target as Node)) {
                setNotificationsOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const { t } = useTranslation();

    const navItems = [
        { to: '/dashboard', icon: LayoutDashboard, label: t('nav.dashboard') },
        { to: '/goals', icon: Target, label: t('nav.goals') },
        { to: '/boards', icon: Kanban, label: t('nav.boards') },
        { to: '/notes', icon: FileText, label: t('nav.notes', 'Notes') },
        { to: '/analytics', icon: BarChart3, label: t('nav.analytics') },
        { to: '/reports', icon: PieChart, label: t('nav.reports', 'Reports') },
        { to: '/alignment', icon: Link2, label: t('nav.alignment', 'Alignment') },
    ];

    return (
        <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900">
            {/* Mobile sidebar backdrop */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside
                className={clsx(
                    'fixed inset-y-0 left-0 z-50 w-64 bg-white dark:bg-neutral-800 border-r border-neutral-200 dark:border-neutral-700 transform transition-transform lg:translate-x-0',
                    sidebarOpen ? 'translate-x-0' : '-translate-x-full'
                )}
            >
                {/* Logo */}
                <div className="h-16 flex items-center justify-between px-4 border-b border-neutral-200 dark:border-neutral-700">
                    <div className="flex items-center gap-2">
                        <Target className="w-8 h-8 text-primary-600" />
                        <span className="text-lg font-bold gradient-text">VisionBoard</span>
                    </div>
                    <button
                        onClick={() => setSidebarOpen(false)}
                        className="lg:hidden p-1 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Workspace selector */}
                <div className="p-4 border-b border-neutral-200 dark:border-neutral-700">
                    <WorkspaceSwitcher />
                </div>

                {/* Navigation */}
                <nav className="p-4 space-y-1">
                    {navItems.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            className={({ isActive }) =>
                                clsx(
                                    'flex items-center gap-3 px-3 py-2 rounded-lg transition-colors',
                                    isActive
                                        ? 'bg-primary-50 dark:bg-primary-600/20 text-primary-600 font-medium'
                                        : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700'
                                )
                            }
                        >
                            <item.icon className="w-5 h-5" />
                            {item.label}
                        </NavLink>
                    ))}

                    {/* Divider */}
                    <div className="border-t border-neutral-200 dark:border-neutral-700 my-3" />

                    {/* Referral link */}
                    <NavLink
                        to="/referral"
                        className={({ isActive }) =>
                            clsx(
                                'flex items-center gap-3 px-3 py-2 rounded-lg transition-colors',
                                isActive
                                    ? 'bg-primary-50 dark:bg-primary-600/20 text-primary-600 font-medium'
                                    : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700'
                            )
                        }
                    >
                        <UserPlus className="w-5 h-5" />
                        {t('nav.referral', 'Mời bạn bè')}
                    </NavLink>

                    {/* Pricing link - only show for FREE users */}
                    {user?.plan !== 'pro' && (
                        <NavLink
                            to="/pricing"
                            className={({ isActive }) =>
                                clsx(
                                    'flex items-center gap-3 px-3 py-2 rounded-lg transition-colors',
                                    isActive
                                        ? 'bg-amber-50 dark:bg-amber-600/20 text-amber-600 font-medium'
                                        : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700'
                                )
                            }
                        >
                            <Crown className="w-5 h-5 text-amber-500" />
                            {t('nav.pricing', 'Nâng cấp PRO')}
                        </NavLink>
                    )}

                    {/* Divider */}
                    <div className="border-t border-neutral-200 dark:border-neutral-700 my-3" />

                    {/* Feedback link */}
                    <NavLink
                        to="/feedback"
                        className={({ isActive }) =>
                            clsx(
                                'flex items-center gap-3 px-3 py-2 rounded-lg transition-colors',
                                isActive
                                    ? 'bg-primary-50 dark:bg-primary-600/20 text-primary-600 font-medium'
                                    : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700'
                            )
                        }
                    >
                        <MessageSquare className="w-5 h-5" />
                        {t('nav.feedback', 'Feedback')}
                    </NavLink>

                    {/* Docs link */}
                    <a
                        href="/docs"
                        target="_blank"
                        className="flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                    >
                        <BookOpen className="w-5 h-5" />
                        {t('docs.title')}
                    </a>
                </nav>

                {/* User section */}
                <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-neutral-200 dark:border-neutral-700">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-primary-100 dark:bg-primary-600/20 rounded-full flex items-center justify-center">
                            <span className="text-primary-600 font-semibold">
                                {user?.full_name?.charAt(0) || 'U'}
                            </span>
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{user?.full_name}</p>
                            <p className="text-xs text-neutral-500 truncate">{user?.email}</p>
                        </div>
                        <button
                            onClick={handleLogout}
                            className="p-2 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg"
                            title="Logout"
                        >
                            <LogOut className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            </aside>

            {/* Main content */}
            <div className="lg:pl-64">
                {/* Top bar */}
                <header className="h-16 bg-white dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-700 flex items-center justify-between px-4 lg:px-6">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => setSidebarOpen(true)}
                            className="lg:hidden p-2 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg"
                        >
                            <Menu className="w-5 h-5" />
                        </button>

                        {/* Search */}
                        <div className="hidden sm:flex items-center gap-2 px-3 py-2 bg-neutral-100 dark:bg-neutral-700 rounded-lg w-64">
                            <Search className="w-4 h-4 text-neutral-400" />
                            <input
                                type="text"
                                placeholder="Search..."
                                className="bg-transparent border-none outline-none text-sm w-full"
                            />
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        {/* Plan Badge */}
                        <PlanBadgeWithUsage />

                        {/* Docs link */}
                        <a
                            href="/docs"
                            target="_blank"
                            className="p-2 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg"
                            title={t('docs.title')}
                        >
                            <BookOpen className="w-5 h-5 text-neutral-600 dark:text-neutral-400" />
                        </a>

                        {/* Theme Toggle */}
                        <ThemeToggle />

                        {/* Notifications */}
                        <div className="relative" ref={notificationsRef}>
                            <NotificationsBell
                                isOpen={notificationsOpen}
                                onToggle={() => setNotificationsOpen(!notificationsOpen)}
                            />
                        </div>
                        <button
                            onClick={() => navigate('/settings')}
                            className="p-2 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg"
                        >
                            <Settings className="w-5 h-5 text-neutral-600 dark:text-neutral-400" />
                        </button>
                    </div>
                </header>

                {/* Page content */}
                <main className="p-4 lg:p-6">
                    <Outlet />
                </main>
            </div>

            {/* AI Chat Widget */}
            <AIChatWidget />

            {/* Onboarding Tour */}
            <OnboardingTour />

            {/* Idle Timeout Warning Modal */}
            <IdleTimeoutWarning
                show={showWarning}
                remainingTime={remainingTime}
                onExtend={extendSession}
                onLogout={handleLogout}
            />
        </div>
    );
}

function NotificationsBell({ isOpen, onToggle }: { isOpen: boolean; onToggle: () => void }) {
    const queryClient = useQueryClient();

    const { data } = useQuery({
        queryKey: ['notifications'],
        queryFn: () => api.getNotifications(),
        refetchInterval: 30000, // Refresh every 30 seconds
    });

    const markAllReadMutation = useMutation({
        mutationFn: () => api.markAllNotificationsRead(),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
        },
    });

    const markReadMutation = useMutation({
        mutationFn: (id: string) => api.markNotificationRead(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
        },
    });

    const unreadCount = data?.unread_count || 0;
    const notifications = data?.data || [];

    return (
        <>
            <button
                onClick={onToggle}
                className="p-2 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg relative"
            >
                <Bell className="w-5 h-5 text-neutral-600 dark:text-neutral-400" />
                {unreadCount > 0 && (
                    <span className="absolute top-1 right-1 w-4 h-4 bg-danger-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                        {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                )}
            </button>

            {isOpen && (
                <div className="absolute right-0 top-full mt-2 w-80 bg-white dark:bg-neutral-800 rounded-xl shadow-xl border border-neutral-200 dark:border-neutral-700 z-50">
                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200 dark:border-neutral-700">
                        <h3 className="font-semibold">Notifications</h3>
                        {unreadCount > 0 && (
                            <button
                                onClick={() => markAllReadMutation.mutate()}
                                className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1"
                            >
                                <CheckCheck className="w-3.5 h-3.5" />
                                Mark all read
                            </button>
                        )}
                    </div>

                    {/* Notifications list */}
                    <div className="max-h-80 overflow-y-auto">
                        {notifications.length === 0 ? (
                            <div className="p-6 text-center text-neutral-500">
                                <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                <p className="text-sm">No notifications yet</p>
                            </div>
                        ) : (
                            notifications.slice(0, 8).map((notif) => (
                                <div
                                    key={notif.id}
                                    className={clsx(
                                        'px-4 py-3 hover:bg-neutral-50 dark:hover:bg-neutral-700/50 border-b border-neutral-100 dark:border-neutral-700 last:border-0',
                                        !notif.is_read && 'bg-primary-50/50 dark:bg-primary-600/10'
                                    )}
                                >
                                    <div className="flex items-start gap-3">
                                        <div className={clsx(
                                            'w-2 h-2 mt-2 rounded-full flex-shrink-0',
                                            notif.is_read ? 'bg-neutral-300' : 'bg-primary-500'
                                        )} />
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium">{notif.title}</p>
                                            <p className="text-xs text-neutral-500 line-clamp-2">{notif.message}</p>
                                            <p className="text-xs text-neutral-400 mt-1">
                                                {new Date(notif.created_at).toLocaleDateString()}
                                            </p>
                                        </div>
                                        {!notif.is_read && (
                                            <button
                                                onClick={() => markReadMutation.mutate(notif.id)}
                                                className="p-1 text-neutral-400 hover:text-primary-600"
                                                title="Mark as read"
                                            >
                                                <Check className="w-4 h-4" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </>
    );
}

// PlanBadge wrapper that gets usage from WorkspaceContext
function PlanBadgeWithUsage() {
    const { memberCount, memberLimit } = useWorkspace();
    const { user } = useAuthStore();

    // Get plan from user, default to 'free'
    const plan = user?.plan || 'free';

    return (
        <PlanBadge
            plan={plan}
            usage={{
                members: { used: memberCount, limit: memberLimit },
            }}
        />
    );
}

// Wrapper component that provides WorkspaceContext
export default function AppLayoutWithProvider() {
    return (
        <WorkspaceProvider>
            <AppLayoutContent />
        </WorkspaceProvider>
    );
}
